import apiFn from 'common/services/api-service';

import { getInternalEventId } from '../service';

const api = eventId => apiFn(`/api/uapi/events/${eventId}/integrations`);

/**
 * Create a new integration
 * @param {string} eventId - The event ID
 * @param {string} integrationType - The type of integration (eventbrite, zoom, etc.)
 * @returns {Promise} Integration data
 */
export const create = (eventId, integrationType) =>
  api(eventId)
    .post(null, { type: integrationType })
    .then(response => response.data.data);

/**
 * List integrations for an event
 * @param {string} eventId - The event ID
 * @param {string} type - Optional integration type filter
 * @returns {Promise} Array of integrations
 */
export const list = (eventId, type = null) =>
  api(eventId)
    .get('', { params: { type } })
    .then(response => response.data.data);

/**
 * Authenticate an integration with OAuth code
 * @param {string} eventId - The event ID
 * @param {string} integrationId - The integration ID
 * @param {string} code - The OAuth authorization code
 * @returns {Promise} Authentication result
 */
export const authenticate = (eventId, integrationId, code) =>
  api(eventId)
    .post(`/${integrationId}/authenticate`, { accessCode: code })
    .then(response => response.data.data);

/**
 * Create integration and handle OAuth authentication
 * @param {string} integrationType - The type of integration
 * @param {string} code - The OAuth authorization code
 * @returns {Promise} Authentication result
 */
export const createAndAuthenticate = async (integrationType, code) => {
  const eventId = getInternalEventId();
  if (!eventId) {
    throw new Error('Event ID not found in localStorage');
  }

  const integrations = await list(eventId);
  const existingIntegration = integrations.find(item => item.type === integrationType);

  // If the integration already exists, use that one - otherwise create a new one
  const integration = existingIntegration ?? (await create(eventId, integrationType));
  if (!integration || !integration.id) {
    throw new Error('Failed to create or find integration');
  }

  // Authenticate the integration
  return authenticate(eventId, integration.id, code);
};
